import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { useAuth } from '../AuthProvider';
import { getApiToken } from '../authUtils';
import { backend_api_base_url } from '../config/urlconfig';

const EMPTY = { permissions: new Set(), roles: [], loading: false };

const PermissionsContext = createContext({
  ...EMPTY,
  loading: true,
  can: () => false,
  refresh: () => {},
});

/**
 * Loads the signed-in user's roles and permissions from /api/v2/me/permissions once per login.
 * Fails closed: if the call fails the user simply sees no restricted menus or actions.
 */
export const PermissionsProvider = ({ children }) => {
  const { instance } = useMsal();
  const { account, isLoadingAuth } = useAuth();
  const [state, setState] = useState({ ...EMPTY, loading: true });
  const accountKey = account?.homeAccountId || account?.username || null;

  // A background refresh (after an admin edits roles) keeps the current permissions on screen;
  // flipping `loading` would make route guards unmount the page the admin is working in.
  const load = useCallback(async ({ background = false } = {}) => {
    if (!account) {
      setState(EMPTY);
      return;
    }
    if (!background) setState(prev => ({ ...prev, loading: true }));
    try {
      const token = await getApiToken(instance, account);
      const res = await fetch(`${backend_api_base_url}/api/v2/me/permissions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`API ${res.status}`);
      const data = await res.json();
      setState({ permissions: new Set(data.permissions || []), roles: data.roles || [], loading: false });
    } catch (err) {
      console.error('Failed to load permissions:', err);
      if (!background) setState(EMPTY);
    }
    // accountKey stands in for account: the account object is recreated on MSAL events.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance, accountKey]);

  useEffect(() => {
    if (!isLoadingAuth) load();
  }, [isLoadingAuth, load]);

  const value = useMemo(() => ({
    ...state,
    can: (permission) => state.permissions.has(permission),
    refresh: () => load({ background: true }),
  }), [state, load]);

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
};

export const usePermissions = () => useContext(PermissionsContext);
